<template>
    <div class="email-onboarding-domain flex-column">
        <slot>
            <div class="typographies-heading-h2">
                {{ $l10n.__('Paramètres de configuration') }}
            </div>
        </slot>
        <div class="flex-column" style="gap: 8px">
            <div class="typographies-paragraph-medium-regular information-text">
                {{
                    $l10n.__(
                        'La sécurité des emails est essentielle, un domaine mal configuré peut entrainer un problème de réception de vos emails. C’est pourquoi, nous vous conseillons fortement d’authentifier votre domaine auprès de votre hébergeur afin d’améliorer la délivrabilité de vos futurs emails.',
                    )
                }}
            </div>
            <div class="typographies-paragraph-medium-semibold email-onboarding-domain__caption">
                {{ $l10n.__('Besoin d’aide ? Consultez notre tutoriel dédié : ') }}
                <CompassLink
                    :label="$t.__('email.EmailOnboardingDomain.help_domain_authentication')"
                    link="https://help.sellsy.com/hc/receiving-and-sending-emails-with-sellsy/set-up-your-domain-for-better-deliverability?searched=true"
                    target="blank"
                />
            </div>
        </div>
        <div
            class="flex-row gray-0-bkg p-3 ai-center jc-space email-onboarding-domain__validate-domain"
        >
            <div class="typographies-paragraph-medium-semibold">
                {{ $l10n.__('Votre nom de domaine à authentifier') }}
            </div>
            <CompassInput :disabled="true" :modelValue="domain" />
        </div>
        <div>
            <div class="typographies-heading-h3">{{ $l10n.__('Configuration requise') }}</div>
            <div class="email-onboarding-domain__caption typographies-paragraph-medium-regular">
                {{
                    $l10n.__(
                        'Vous devez reprendre les informations suivantes et les communiquer à l’hébergeur responsable de votre domaine. ',
                    )
                }}
            </div>
        </div>
        <CustomTable>
            <template #header>
                <th>
                    <div class="typographies-paragraph-medium-regular">
                        {{ $l10n.__('Type') }}
                    </div>
                </th>
                <th>
                    <div class="typographies-paragraph-medium-regular">
                        {{ $l10n.__('Nom') }}
                    </div>
                </th>
                <th class="col-action"></th>
                <th>
                    <div class="typographies-paragraph-medium-regular">
                        {{ $l10n.__('Valeur') }}
                    </div>
                </th>
                <th class="col-action"></th>
            </template>
            <template #content>
                <tr v-for="data of dnsData" :key="data.id">
                    <td>
                        <div class="typographies-paragraph-medium-regular">
                            {{ data.type }}
                        </div>
                    </td>
                    <td>
                        <div class="typographies-paragraph-medium-regular">
                            {{ data.host }}
                        </div>
                    </td>
                    <td>
                        <SaasClipboard :data="data.host" class="fright" />
                    </td>
                    <td>
                        <div class="typographies-paragraph-medium-regular">
                            {{ data.data }}
                        </div>
                    </td>
                    <td>
                        <SaasClipboard :data="data.data" class="fright" />
                    </td>
                </tr>
            </template>
        </CustomTable>
        <div class="typographies-paragraph-medium-regular">
            {{
                $l10n.__(
                    'Une fois la configuration communiquée à votre hébergeur, cela peut prendre parfois quelques heures pour l’activation et l’application.\nVous pouvez connaitre le statut de votre domaine dans Réglages > Emails > Paramètres des emails.',
                )
            }}
        </div>
        <CompassAlert
            :description="
                $t.__('email.EmailOnboardingDomain.no_take_step_emails_may_not_be_received')
            "
            :showClose="false"
            class="mb-0"
            type="warning"
        />
        <slot name="actions">
            <div class="flex-level flex-end email-onboarding-domain__container-buttons-submit mt-2">
                <CompassButton
                    :label="$t.__('email.EmailOnboardingDomain.do_this_later')"
                    type="secondary"
                    @click="$emit('validDomain', { appliedConf: false })"
                />
                <CompassButton
                    :label="$t.__('email.EmailOnboardingDomain.apply_configuration_on_domain')"
                    @click="$emit('validDomain', { appliedConf: true })"
                />
            </div>
        </slot>
    </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import OnboardingEmailApi from 'api/onboarding/email/onboardingEmail.js';
import CustomTable from 'js/vue/components/utilities/CustomTable.vue';
import SaasClipboard from 'vcomponents/utilities/SaasClipboard.vue';

const { mapState: mapStateUser } = createNamespacedHelpers('user');

export default {
    name: 'EmailOnboardingDomain',
    components: {
        SaasClipboard,
        CustomTable,
    },
    data() {
        return {
            dnsData: [],
        };
    },
    computed: {
        ...mapStateUser(['email', 'senderEmail']),
        /**
         * Return the domain from the user's senderEmail or people's mail
         *
         * @return {string}
         */
        domain() {
            return (this.senderEmail || this.email).split('@').pop() ?? '';
        },
    },
    created() {
        this.findDnsData();
    },
    methods: {
        /**
         * Find all dns according user email
         */
        findDnsData() {
            OnboardingEmailApi.findDns({ email: this.senderEmail || this.email }).then(
                (res) => {
                    this.dnsData = Object.values(res.dns);
                     
                    EventBus.emit(
                        'EmailOnboardingDomain::OnboardingEmailApi::findDns::isValidDomainAuth',
                        res.valid,
                    );
                },
                (err) => console.error(`OnboardingEmailApi::findDns`, err),
            );
        },
    },
};
</script>

<style lang="scss" scoped>
@use '~@sellsy/styleguide/src/_variables' as *;

.email-onboarding-domain {
    gap: 16px;
    &__help {
        color: var(--colors-common-text-standard-caption);
    }
    .information-text {
        max-width: 580px;
    }

    .action-info {
        gap: 8px;
    }

    .el-input.is-disabled .el-input__inner {
        background-color: $gray-05;
        border-color: $gray-40;
        color: $gray-60;
    }

    &__container-buttons-submit {
        gap: 16px;
    }

    .custom-table .col-action {
        width: 57px;
    }

    &__validate-domain {
        border-radius: 4px;

        .compass-input {
            min-width: 280px;
        }
    }
}
</style>
